import Events from '@utilities/events';

const TOGGLE_ACTIVE_CLASS = 'toggle--is-active';
const BUTTON_LABEL_CLASS = 'button__label';
const BUTTON_ICON_CLASS = 'button__icon';
const HIDDEN_CLASS = 'u-hidden';
const DATA_ATTRIBUTE_DEFAULT_ACTIVE = 'data-toggle-default-active';

const MINUS_ICON =
  '<svg focusable="false" class="c-icon icon--minus" viewBox="0 0 18 1" width="18px" height="1px"><rect fill="currentColor" width="18" height="1"></rect></svg>';

class Toggle {
  constructor(element) {
    this.element = element;
    this.controls = element.getAttribute('aria-controls');
    this.id = element.id || this.controls;
    this.links = this.getToggleLinks();
    this.activeClass = element.dataset.toggleActiveClass || TOGGLE_ACTIVE_CLASS;
    this.linksActiveClass = element.dataset.linksActiveClass || this.activeClass;
    this._isActive = this.element.classList.contains(this.activeClass);
    this.ignoreInteractiveElements = this.element.dataset.ignoreInteractiveElements || false;
    this.checkLines = element.dataset.checkLines || false;

    // Label
    this.label = element.querySelector(`.${BUTTON_LABEL_CLASS}`) || false;
    this.activeLabel = element.dataset.toggleActiveLabel || false;
    this.inActiveLabel = this.label?.innerHTML;

    // Icon
    this.noToggleIcon = element.dataset.notoggleicon;
    this.icon = element.querySelector(`.${BUTTON_ICON_CLASS}`) || false;
    this.activeIcon = MINUS_ICON;
    this.inActiveIcon = this.icon?.innerHTML;

    if (!this.ignoreInteractiveElements) {
      this.interactiveElements = Toggle.getInteractiveElements(this.links);
    }

    if (!element.dataset.checkLines && this.hideCta()) {
      this.element.setAttribute(DATA_ATTRIBUTE_DEFAULT_ACTIVE, true);
      this.element.classList.add(HIDDEN_CLASS);
    }

    this.bindEvents();
    this.setDefaultState();
  }

  get isActive() {
    return this._isActive;
  }

  set isActive(boolean) {
    this._isActive = boolean;
  }

  /**
   * Find all interactive children in a toggleable element that are hidden when toggled
   * @param elementsToToggle, array with all elements controlled by the toggle
   */
  static getInteractiveElements(elementsToToggle) {
    const itemsBelowToggle = [];

    elementsToToggle.forEach((element) => {
      const parentHeight = element.clientHeight;
      const parentOffsetTop = element.offsetTop; // In case parent isn't relatively positioned
      const interactiveChildren = element.querySelectorAll(
        'a, area, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, video',
      );

      const hiddenElements = [...interactiveChildren].filter(
        (childElement) => childElement.offsetTop - parentOffsetTop >= parentHeight,
      );

      itemsBelowToggle.push(...hiddenElements);
    });

    return itemsBelowToggle;
  }

  /**
   * Set tab index for DOM elements
   * @param elements, elements to set index on
   * @param value, value to be set
   */
  static setTabIndex(elements, value) {
    elements.forEach((element) => {
      element.tabIndex = value
    });
  }

  /**
   * Hide cta if text is not overflowing and functionality is not triggered
   * For elements outside visibility, the check should be done with events when it becomes visible
   */
  hideCta() {
    let hideCta = false;
    this.links.forEach((link) => {
      if (link.scrollHeight && link.scrollHeight === link.clientHeight) hideCta = true;
    });
    return hideCta;
  }

  /**
   * Bind all events
   */
  bindEvents() {
    this.element.addEventListener('click', (event) => {
      this.toggleState();

      if (this.element.dataset.togglePreventDefault) {
        event.preventDefault();
      }
    });

    Events.$on(`toggle[${this.id}]::reload`, () => {
      this.element.classList[this.hideCta() ? 'add' : 'remove'](HIDDEN_CLASS);
    });

    Events.$on(`toggle[${this.id}]::toggle`, (event) => {
      this.toggleState();
      event.preventDefault();
    });

    Events.$on(`toggle[${this.id}]::close`, (event) => {
      if (this.isActive) {
        this.toggleState();
        event.preventDefault();
      }
    });

    Events.$on(`toggle[${this.id}]::check-lines`, () => {
      // Reset to collapsed state
      if (this.isActive) {
        this.toggleState();
      }

      // Then check again
      this.element.classList[this.hideCta() ? 'add' : 'remove'](HIDDEN_CLASS);
    });

    Events.$on(`toggle::check-tabindex`, () => this.checkTabIndex());
  }

  /**
   * Toggles the entire UI state of the toggle component
   */
  toggleState() {
    if (this.element.dataset.toggleLive === 'true') {
      this.links = this.getToggleLinks();
    }

    this.toggleActiveClassNames();
    this.setAccesibilityState();
    this.triggerExternalEvents();

    if (!this.ignoreInteractiveElements) {
      Events.$trigger('toggle::check-tabindex');
    }
  }

  /**
   * Sets default ARIA, tabIndex and classname roles based on config
   */
  setDefaultState() {
    if (this.element.dataset.toggleDefaultActive === 'true') {
      this.toggleState();
    }

    this.setAccesibilityState()
    if (!this.ignoreInteractiveElements) {
      Events.$trigger('toggle::check-tabindex');
    }
  }

  /**
   * Toggles all element class names
   */
  toggleActiveClassNames() {
    this.toggleToggleElementActiveState();
    this.toggleLinksClassNames();
    this.toggleLabels();
    this.toggleIcons();
  }

  /**
   * Toggles the active classname of the toggle component and toggles aria attribute
   */
  toggleToggleElementActiveState() {
    this.element.classList.toggle(this.activeClass);
    this.isActive = this.element.classList.contains(this.activeClass);
  }

  /**
   * Toggles the labels of the toggle button
   */
  toggleLabels() {
    if (!this.activeLabel || !this.label) return;
    this.label.innerHTML = this.isActive ? this.activeLabel : this.inActiveLabel;
  }

  /**
   * Toggles the icons of the toggle button
   */
  toggleIcons() {
    if (this.noToggleIcon || !this.activeIcon || !this.icon) return;

    this.icon.innerHTML = this.isActive ? this.activeIcon : this.inActiveIcon;
  }

  /**
   * Toggles the active classname of the toggle components links
   */
  toggleLinksClassNames() {
    const toggleAction = this.isActive ? 'add' : 'remove';
    this.links.forEach((link) => link.classList[toggleAction](this.linksActiveClass));
  }

  /**
   * Toggles the ARIA attributes
   */
  setAccesibilityState() {
    this.element.setAttribute('aria-expanded', this.isActive.toString());
    /** REMOVED aria-hidden due to ACCESSIBILITY ERRORS 
     * DATA ATTRIBUTE 'data-do-not-aria-hidden" added only to B&B mobile navigation
     * element is already hidden by css
    */
    if(!this.element.hasAttribute('data-do-not-aria-hidden')) {
      this.links.forEach(link => {
        link.setAttribute('aria-hidden', (!this.isActive).toString())
      });
    }
  }

  /**
   * Triggers external events based on new state
   */
  triggerExternalEvents() {
    const newState = this.isActive ? 'opened' : 'closed';
    Events.$trigger(`toggle[${this.id}]::${newState}`);
    Events.$trigger(`toggle[${this.id}]::toggled`, {
      data: this.isActive,
    });
  }

  /**
   * Get all the external link elements from the toggle component
   */
  getToggleLinks() {
    const ariaControls = this.controls;
    if (!ariaControls) return [];

    const LINKS_SELECTOR = ariaControls
    .split(/[ ,]+/)
    .map((id) => `#${id}`)
    .join(', ');
  
    return [...document.querySelectorAll(LINKS_SELECTOR)];
  }

  /**
   * Set initial tab index
   */
  checkTabIndex() {
    Toggle.setTabIndex(this.interactiveElements, this._isActive ? 0 : -1);
  }
}

export default Toggle;
